import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import type { DeliveryChannelCounts, DeliveryCheckable, PersonWithContact, PhoneChannelCheckable, ResolvedPhoneChannel } from './deliveryChannel.types';

// ── Phone validity ──────────────────────────────────────────────────
// Single source of truth for "can we SMS this person?" — guards against
// malformed numbers and non-Latin unicode that carriers reject.

export const hasValidPhone = (phoneDisplay: string | null | undefined): boolean => !!phoneDisplay && isValidPhoneNumber(phoneDisplay) && !/[\u0100-\uffff]/.test(phoneDisplay);

// ── Phone channel ───────────────────────────────────────────────────

// Mirrors email_service's `TWILIO_SMS_SUPPORTED_COUNTRIES`: its send-time selector texts these and
// sends every other country by WhatsApp. It reads the libphonenumber region, not the calling code,
// so +1 numbers in Puerto Rico, Jamaica and the rest of the NANP outside the US and Canada go by
// WhatsApp too.
const SMS_COUNTRIES: ReadonlySet<CountryCode> = new Set<CountryCode>(['US', 'CA']);

/**
 * Which channel a guest's phone gets (MSG-584). Reads only an E.164 form: a raw number the host
 * typed can lack a country, and a phone the backend could not sanitize may not receive a message on
 * either channel, so it reports `invalid` rather than guessing. Validates as well as parses, because
 * libphonenumber places a truncated number such as `+3361` in a country.
 */
export const resolvePhoneChannel = ({ hasPhone, e164 }: Readonly<{ hasPhone: boolean; e164?: string | null }>): ResolvedPhoneChannel => {
  if (!hasPhone) return { channel: 'none' };
  const parsed = e164 ? parsePhoneNumberFromString(e164) : undefined;
  if (!parsed?.country || !parsed.isValid()) return { channel: 'invalid' };
  return { channel: SMS_COUNTRIES.has(parsed.country) ? 'sms' : 'whatsapp', countryCode: `+${parsed.countryCallingCode}` };
};

// ── Delivery-channel predicates ──────────────────────────────────────

export const isEmailRecipient = (p: DeliveryCheckable): boolean => !!p.hasEmailDeliveryMethodSelected;

export const isSmsRecipient = (p: DeliveryCheckable): boolean => !!p.hasTextDeliveryMethodSelected;

// A preselected reminder audience carries only `phoneDisplay`, never the picker's `phoneChannelE164`, so a
// `phoneDisplay` that already validates stands in for it, as it does in the picker's own rows. Validation
// needs the international form, so a number with no country still reads as unknown.
const phoneChannelE164Of = (person: PhoneChannelCheckable): string | null | undefined =>
  person.phoneChannelE164 || (hasValidPhone(person.phoneDisplay) ? person.phoneDisplay : null);

const isTextRecipientOn = (person: PhoneChannelCheckable, channel: 'sms' | 'whatsapp'): boolean => {
  const e164 = phoneChannelE164Of(person);
  return isSmsRecipient(person) && resolvePhoneChannel({ hasPhone: !!e164, e164 }).channel === channel;
};

/** Whether this guest gets the text by WhatsApp: on the text channel, with a phone outside the US and Canada. See `countWhatsAppTextRecipients`. */
export const isWhatsAppTextRecipient = (person: PhoneChannelCheckable): boolean => isTextRecipientOn(person, 'whatsapp');

const countTextRecipientsOn = (people: ReadonlyArray<PhoneChannelCheckable>, channel: 'sms' | 'whatsapp'): number =>
  people.filter(person => isTextRecipientOn(person, channel)).length;

/**
 * How many of a selection get the text by WhatsApp: on the text channel, with a phone outside the US
 * and Canada. A prediction of email_service's send-time routing, which can still fall back to SMS,
 * so it decides what the composer previews, never who is sent to.
 */
export const countWhatsAppTextRecipients = (people: ReadonlyArray<PhoneChannelCheckable>): number => countTextRecipientsOn(people, 'whatsapp');

/**
 * The SMS side of `countWhatsAppTextRecipients`: on the text channel, with a phone in the US or Canada.
 * A guest whose number hasn't loaded or doesn't validate counts in neither, since their channel is unknown.
 */
export const countSmsChannelTextRecipients = (people: ReadonlyArray<PhoneChannelCheckable>): number => countTextRecipientsOn(people, 'sms');

// ── Per-channel tallies over a selection ─────────────────────────────

/**
 * How many of the selected people each channel can actually reach, and how many it can't reach at
 * all. A person can count toward both channels — on a "both" send they get the text and the email.
 *
 * `smsOnly` contexts (e.g. Text Blast) treat a missing phone as unreachable even when the person
 * has an email, because the email is never sent.
 */
export const countDeliveryChannels = (people: ReadonlyArray<DeliveryCheckable>, { smsOnly = false }: { smsOnly?: boolean } = {}): DeliveryChannelCounts => {
  let textCount = 0;
  let emailCount = 0;
  let unreachableCount = 0;
  for (const person of people) {
    const hasSms = isSmsRecipient(person);
    const hasEmail = isEmailRecipient(person);
    if (hasSms) textCount++;
    if (hasEmail) emailCount++;
    if (smsOnly ? !hasSms : !hasSms && !hasEmail) unreachableCount++;
  }
  return { textCount, emailCount, unreachableCount };
};

// ── Recompute per-person flags when the global delivery method changes ──

/**
 * No contact info has ever been attached to this selection entry: a `?personIds` /
 * `?selectedGuestKeys` seed (`{ id }` / `{ personIdTag }`) the picker hasn't hydrated yet — or never
 * will, if its contacts read fails — or a resumed recipient the contact lookup couldn't find.
 *
 * Its delivery flags say nothing about reachability, when it has any: `recomputeDeliveryFlags` reads
 * an absent `email` as "no email", so the first delivery-method change flags a seed exactly like a
 * hydrated guest who genuinely has none. What tells them apart is that every hydration path writes
 * contact fields — both pickers always write `hasPhone`, and the resume lookup writes `email` and
 * `phoneDisplay` — so their joint absence is the signal.
 */
export const isContactInfoUnknown = (p: PersonWithContact): boolean => p.email === undefined && p.phoneDisplay === undefined && p.hasPhone === undefined;

/**
 * Recomputes `hasEmailDeliveryMethodSelected` / `hasTextDeliveryMethodSelected`
 * for every person based on the current delivery method selection.
 *
 * Mirrors `applyChannelDefaults` from the PeoplePicker controller but operates
 * on the PeopleContext data shape rather than PeoplePicker view-items.
 */
export const recomputeDeliveryFlags = <T extends PersonWithContact>(people: ReadonlyArray<T>, deliveryMethod: 'text' | 'email' | 'both'): ReadonlyArray<T> =>
  people.map(person => {
    const hasPhone = hasValidPhone(person.phoneDisplay) || !!person.hasPhone;
    const hasEmail = !!person.email;

    switch (deliveryMethod) {
      case 'text':
        // Text-only: people without a phone are unreachable — no email fallback.
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: false };
      case 'email':
        // Email-only: people without an email are unreachable — no text fallback.
        return { ...person, hasEmailDeliveryMethodSelected: hasEmail, hasTextDeliveryMethodSelected: false };
      case 'both':
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: hasEmail };
    }
  });

// ── Session-persisted delivery method ────────────────────────────────

import { getSessionStorage } from '@shared/core/storage';

const VALID_METHODS = new Set(['text', 'email', 'both'] as const);
type ValidMethod = 'text' | 'email' | 'both';

function readPersisted(key: string): ValidMethod | undefined {
  try {
    const raw = getSessionStorage().getItem(key);
    if (raw && VALID_METHODS.has(raw as ValidMethod)) return raw as ValidMethod;
  } catch {
    // best-effort
  }
  return undefined;
}

function persist(key: string, value: ValidMethod): void {
  try {
    getSessionStorage().setItem(key, value);
  } catch {
    // best-effort
  }
}

export const readPersistedDeliveryMethod = (storageKey: string): ValidMethod => readPersisted(`deliveryMethod:${storageKey}`) ?? 'both';

export const persistDeliveryMethod = (storageKey: string, method: ValidMethod): void => persist(`deliveryMethod:${storageKey}`, method);

/** Read the persisted "if both are available, send via:" preference (separate from delivery method). */
export const readPersistedPreference = (storageKey: string): ValidMethod | undefined => readPersisted(`preference:${storageKey}`);

export const persistPreference = (storageKey: string, method: ValidMethod): void => persist(`preference:${storageKey}`, method);

/**
 * Recomputes per-person delivery flags when the "if both available" preference changes.
 * Uses fallback logic: the preferred channel is tried first; the other channel is used
 * as a fallback for guests who only have one contact method available.
 */
export const recomputeDeliveryFlagsWithPreference = <T extends PersonWithContact>(people: ReadonlyArray<T>, preference: 'text' | 'email' | 'both'): ReadonlyArray<T> =>
  people.map(person => {
    const hasPhone = hasValidPhone(person.phoneDisplay) || !!person.hasPhone;
    const hasEmail = !!person.email;
    switch (preference) {
      case 'text':
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: !hasPhone && hasEmail };
      case 'email':
        return { ...person, hasEmailDeliveryMethodSelected: hasEmail, hasTextDeliveryMethodSelected: !hasEmail && hasPhone };
      case 'both':
        return { ...person, hasTextDeliveryMethodSelected: hasPhone, hasEmailDeliveryMethodSelected: hasEmail };
    }
  });

export const splitByDeliveryChannel = <T extends DeliveryCheckable>(
  people: ReadonlyArray<T>,
): Readonly<{ emailRecipients: ReadonlyArray<T>; smsRecipients: ReadonlyArray<T> }> => ({
  emailRecipients: people.filter(isEmailRecipient),
  smsRecipients: people.filter(isSmsRecipient),
});
